import { HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../core/configs/service-endpoints';
import { ServiceResult } from '../../core/interfaces/service-result';
import { BaseCrudService } from '../../core/services/base-crud.service';
import { I18nService } from '../../core/services/i18n.service';
import { PaymentDataCreate } from '../dtos/payment-type/payment-data-create';
import { PaymentDataUpdate } from '../dtos/payment-type/payment-data-update';
import { PaymentMethodName } from '../enums/payment/payment-method-name';
import { OrganisationData } from '../interfaces/organisation/organisation-data';
import { BasePaymentMethodData } from '../interfaces/payment/base-payment-method-data';
import { CustomerPaymentMethodData } from '../interfaces/payment/customer-payment-method-data';
import { PaymentData } from '../interfaces/payment/payment-data';

@Injectable({
  providedIn: 'root'
})
export class PaymentDataService extends BaseCrudService<PaymentDataCreate, PaymentDataUpdate | string[]> {

  baseUrl = this.runtimeConfig.dataBackend;

  selectedOrganisationId: string;
  selectedOrganisationName: string;
  selectedOrganisationData: OrganisationData;
  paymentMethodConfig: BasePaymentMethodData[] = [];
  paymentDataInitialised = new Subject<boolean>();

  /**
   * Tenant Payment Data Objects
   */
  paymentMethods: CustomerPaymentMethodData[] = [];
  paymentTypes: PaymentData[] = [];


  constructor(private i18n: I18nService,
              private messageService: MessageService) {
    super();
  }

  /**
   * Initialize/Refresh the payment methods and payment types
   * for the currently logged-in customer
   */
  initTenantPaymentData(): Observable<boolean> {
    /**
     * Init Payment-Methods
     */
    this.findAll(SERVICE_ENDPOINTS.GET_PAYMENT_METHODS).subscribe({
      next: result => {
        if (result.successful) {
          /**
           * init special payment-method params
           * hasPaymentTypes = true if sum of paymentTypeCount-params is greater 0
           * closed = true if method has only closed types in paymentTypeCount
           */
          result.payload.map(method => {
            method.hasPaymentTypes = (method?.paymentTypeCount?.TEST + method?.paymentTypeCount?.LIVE + method.paymentTypeCount?.CLOSED) > 0;
            method.closed = (method?.paymentTypeCount?.TEST === 0) && (method?.paymentTypeCount?.LIVE === 0) && (method?.paymentTypeCount?.CLOSED > 0);
          })
          this.paymentMethods = result.payload;
          /**
           * Init Payment-Types
           */
          this.findAll(SERVICE_ENDPOINTS.GET_PAYMENT_TYPES).subscribe({
            next: result => {
              if (result.successful) {
                this.paymentTypes = result.payload;
              } else {
                this.paymentTypes = [];
                this.messageService.add({
                  sticky: true,
                  severity: 'error',
                  summary: this.i18n.i18nLng()['HTTPDATAERROR'],
                  detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
                });
              }
              this.paymentDataInitialised.next(true);
            }, error: err => {
              this.paymentTypes = [];
              this.messageService.add({
                sticky: true,
                severity: 'error',
                summary: this.i18n.i18nLng()['HTTPDATAERROR'],
                detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
              });
              this.paymentDataInitialised.next(true);
            }
          })
        } else {
          this.paymentMethods = [];
          this.paymentTypes = [];
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
          this.paymentDataInitialised.next(true);
        }
      }, error: err => {
        this.paymentMethods = [];
        this.paymentTypes = [];
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
        this.paymentDataInitialised.next(true);
      }
    })
    return this.paymentDataInitialised.asObservable();
  }

  /**
   * get payment types, with LazyLoading-Params (paging), for a specific organisation
   * used in admin-portal
   * @param lazyLoadEvent
   * @param tenantId
   */
  findPageAdmin(lazyLoadEvent: LazyLoadEvent, tenantId: string): Observable<ServiceResult> {
    let params = new HttpParams().set('search', JSON.stringify(lazyLoadEvent));
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<ServiceResult>(this.baseUrl + SERVICE_ENDPOINTS.GET_PAYMENT_TYPES_TENANT + tenantId, {headers, params});
  }

  /**
   * get payment types, with LazyLoading-Params (paging), for a specific organisation
   * used in lead desk for all organisations of the lead
   * @param lazyLoadEvent
   * @param tenantId
   */
  findPageLead(lazyLoadEvent: LazyLoadEvent, tenantId: string): Observable<ServiceResult> {
    let params = new HttpParams().set('search', JSON.stringify(lazyLoadEvent));
    let headers = new HttpHeaders().set('content-type', 'application/json; charset=utf-8');
    return this.http.get<ServiceResult>(this.baseUrl + SERVICE_ENDPOINTS.GET_PAYMENT_TYPES_LEAD + tenantId, {headers, params});
  }

  /**
   * set icon and provider data for specific payment type
   * @param paymentDataDto
   */
  setIconAndProvider(paymentDataDto: PaymentDataCreate): PaymentDataCreate {
    // TODO provider and acquirer for all payment methods
    switch (paymentDataDto.paymentMethod) {
      case PaymentMethodName.CREDITCARD:
        paymentDataDto.icon = 'pi pi-credit-card';
        paymentDataDto.acquirer = 'PAYONE GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.CREDITCARD_MOTO:
        paymentDataDto.customIcon = true;
        paymentDataDto.icon = 'creditcard_moto';
        paymentDataDto.acquirer = 'PAYONE GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.SEPA:
        paymentDataDto.customIcon = true;
        paymentDataDto.icon = 'sepa';
        paymentDataDto.acquirer = 'S-Public Services GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.SEPA_BLACKLIST:
        paymentDataDto.customIcon = true;
        paymentDataDto.icon = 'sepa_blacklist';
        paymentDataDto.acquirer = 'S-Public Services GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.KLARNA:
        paymentDataDto.customIcon = true;
        paymentDataDto.icon = 'klarna';
        break;
      case PaymentMethodName.DIRECTTRANSFER:
        paymentDataDto.customIcon = true;
        paymentDataDto.icon = 'directtransfer';
        paymentDataDto.acquirer = 'S-Public Services GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.PAYPAL:
        paymentDataDto.icon = 'pi pi-paypal';
        paymentDataDto.acquirer = 'PayPal';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.APPLEPAY:
        paymentDataDto.icon = 'pi pi-apple';
        paymentDataDto.acquirer = 'PAYONE GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.GOOGLEPAY:
        paymentDataDto.icon = 'pi pi-google';
        paymentDataDto.acquirer = 'PAYONE GmbH';
        paymentDataDto.provider = 'Pagateq (VÖB)';
        break;
      case PaymentMethodName.WERO:
        paymentDataDto.customIcon = true;
        paymentDataDto.icon = 'wero';
        paymentDataDto.acquirer = 'PAYONE GmbH';
        paymentDataDto.provider = 'PAYONE GoPay';
        break;
    }
    return paymentDataDto;
  }
}
